import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const { settings } = useGlobalSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.senha);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-border bg-white shadow-elegant">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="flex flex-col lg:flex-row">
          <div className="relative flex items-center justify-center overflow-hidden bg-gradient-cta p-12 text-white lg:w-1/2">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <div className="absolute -right-20 top-16 h-56 w-56 rounded-full bg-accent/30 blur-3xl" />
            <div className="absolute -left-20 bottom-16 h-56 w-56 rounded-full bg-highlight/20 blur-3xl" />
            <div className="relative text-center">
              <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-3xl border border-white/15 bg-white/10 shadow-soft backdrop-blur-sm">
                {settings?.iconUrl ? (
                  <img
                    src={settings.iconUrl}
                    alt="Ícone do Sistema"
                    className="h-16 w-16 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                ) : (
                  <img
                    src="/assets/default-logo.png"
                    alt="Vox Campaign"
                    className="h-16 w-16 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                )}
              </div>
              <h1 className="mb-4 text-4xl font-bold">
                {settings?.pageTitle || 'Vox Campaign'}
              </h1>
              <p className="mx-auto max-w-md text-primary-foreground/80">
                Otimize sua campanha com o Vox Campaign
              </p>
            </div>
          </div>

          <div className="relative bg-white p-8 sm:p-12 lg:w-1/2">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <h2 className="mb-2 text-3xl font-bold text-foreground">Bem-vindo!</h2>
                <p className="text-muted-foreground">Faça login para acessar sua conta</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground">
                    E-mail
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                    placeholder="seu@email.com"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="senha" className="mb-2 block text-sm font-medium text-foreground">
                    Senha
                  </label>
                  <input
                    {...register('senha')}
                    type="password"
                    id="senha"
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  {errors.senha && (
                    <p className="mt-2 text-sm text-red-600">{errors.senha.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-2xl bg-gradient-primary px-6 py-3 font-semibold text-primary-foreground shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow focus:outline-none focus:ring-2 focus:ring-accent/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Entrando...
                    </div>
                  ) : (
                    'Entrar'
                  )}
                </button>
 
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
